import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Container, Row, Col, Card, Badge, Button, Modal, Tab, Tabs,
} from 'react-bootstrap';
import BillingoService from '../../Services/BillingoService';
import BillingBasisPicker from './Components/BillingBasisPicker';
import InvoicedSummaryTable from './Components/InvoicedSummaryTable';
import AllInvoicesList from './Components/AllInvoicesList';
import { EMPTY_FILTERS } from './Components/InvoiceFilters';
import MissingDocuments from './Components/MissingDocuments';
import NeedsFinalSzamla from './Components/NeedsFinalSzamla';
import FailedDocuments from './Components/FailedDocuments';
import InvoicingKpi from './Components/InvoicingKpi';
import useAskAiSuggestion from '../../Components/ChatWidget/askAiSuggestions';

const isThisMonth = (d) => {
  if (!d) return false;
  const dt = new Date(d);
  const now = new Date();
  return dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
};

// Invoicing workbench: KPIs, a create panel (bill by bookings / event / partner)
// and two working views — every issued document, and the bookings still owed a
// document (including completed trips awaiting their final Számla).
function Invoicing() {
  const [resolved, setResolved] = useState({ bookings: [], label: '', partner: null });
  const [documents, setDocuments] = useState([]);
  const [missingSummary, setMissingSummary] = useState(null);
  const [failedSummary, setFailedSummary] = useState(null);
  // Bumped on every page refresh so the Missing documents tab reloads too.
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab] = useState('all');
  const [error, setError] = useState('');

  /**
   * WHAT TO ASK ON THE INVOICING WORKBENCH — and it depends on the tab,
   * because the tab IS the question. Two branches, both of which map onto a
   * tool the agent already has, so neither is a guess:
   *
   *   needs-final → `bookings_needing_final_invoice`, whose docstring says it
   *     is "exactly the dashboard's Invoicing 'Needs final Számla' tab".
   *   anything else → `list_invoices(document_type='dijbekero',
   *     status='awaiting_payment')`, which is the thing no tab on this screen
   *     shows: the proformas nobody has settled. 83 of them auto-settle when
   *     they convert and 91 were marked paid by hand, so the stragglers have
   *     no safety net and nothing here surfaces them.
   *
   * The prompt asks for the count AND the oldest ten rather than "all of
   * them": every tool result is byte-capped, and a trimmed list reported as
   * the whole answer is a wrong answer. `pagination.total` is the count.
   */
  useAskAiSuggestion(tab === 'needs-final'
    ? {
      label: 'Ask about this queue',
      question: 'Which trips are still waiting for a final Számla?',
      prompt: 'List the completed bookings still waiting for their final Számla, oldest'
        + ' trip first — booking reference, partner, what has already been issued, and'
        + ' the amount still to invoice.',
    }
    : {
      label: 'Ask about invoicing',
      question: 'Which díjbekérős are still unpaid?',
      prompt: 'How many Díjbekérő documents are still awaiting payment, and which are the'
        + ' ten oldest? Give the document number, the customer or partner, the amount'
        + ' with its currency, and the issue date.',
    });

  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [pagination, setPagination] = useState(null);
  const [summary, setSummary] = useState(null);
  const [facets, setFacets] = useState({ created_by: [], currencies: [] });
  const [paginated, setPaginated] = useState(true);

  // Typing in the search box shouldn't fire a request per keystroke.
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q), 300);
    return () => clearTimeout(t);
  }, [filters.q]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await BillingoService.listAllDocuments({
        ...filters, q: debouncedQ, page, limit,
      });
      setDocuments(result.documents);
      setPagination(result.pagination);
      setFacets(result.facets);
      setPaginated(result.paginated);
      // Keep the last known summary when an older API omits it, rather than
      // blanking the KPI strip.
      if (result.summary) setSummary(result.summary);
    } catch (err) {
      // Never let a failed fetch masquerade as "nothing has been invoiced" —
      // an empty list is a real answer, and this is not it.
      setDocuments([]);
      setError(err.response?.data?.message || err.message || 'Could not load invoices.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [debouncedQ, page, limit,
    filters.type, filters.status, filters.created_by, filters.currency,
    filters.from, filters.to, filters.min_total, filters.max_total, filters.uploads]);

  useEffect(() => { refresh(); }, [refresh]);

  // A new or cancelled document changes both views.
  const refreshAll = () => {
    refresh();
    setRefreshKey((k) => k + 1);
  };

  // Any filter change restarts at page 1 — staying on page 7 of a narrower
  // result set would show an empty table.
  const handleFiltersChange = (next) => {
    setFilters(next);
    setPage(1);
  };

  const kpis = summary
    ? {
      total: summary.total,
      awaiting: summary.awaiting,
      paid: summary.paid,
      thisMonth: summary.this_month,
    }
    // Pre-pagination API: fall back to counting the single page it returned.
    : {
      total: documents.length,
      awaiting: documents.filter(
        (d) => d.status === 'awaiting_payment' || d.status === 'partially_paid',
      ).length,
      paid: documents.filter((d) => d.status === 'paid').length,
      thisMonth: documents.filter((d) => isThisMonth(d.created_at)).length,
    };

  return (
    <Container className="py-4">
      <div className="mb-4 d-flex justify-content-between align-items-start">
        <div>
          <h3 className="mb-1">Invoicing</h3>
          <div className="text-muted">Issue and track Hungarian fiscal documents.</div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={refreshAll} disabled={loading} title="Refresh">
            <i className={`fa fa-rotate-right${loading ? ' fa-spin' : ''}`} />
          </Button>
          <Button onClick={() => setShowCreate(true)}>
            <i className="fa fa-plus me-1" />
            Create document
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="d-flex justify-content-between align-items-center">
          <span>
            <i className="fa fa-triangle-exclamation me-2" />
            {error}
          </span>
          <Button size="sm" variant="outline-danger" onClick={refreshAll}>Retry</Button>
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} lg><InvoicingKpi label="Documents" value={kpis.total} icon="fa-file-invoice" accent="#2563eb" /></Col>
        <Col xs={12} sm={6} lg><InvoicingKpi label="Awaiting payment" value={kpis.awaiting} icon="fa-hourglass-half" accent="#d97706" /></Col>
        <Col xs={12} sm={6} lg><InvoicingKpi label="Paid invoices" value={kpis.paid} icon="fa-circle-check" accent="#10b981" /></Col>
        <Col xs={12} sm={6} lg><InvoicingKpi label="This month" value={kpis.thisMonth} icon="fa-calendar-day" accent="#6366f1" /></Col>
        <Col xs={12} sm={6} lg>
          {/* The one KPI that is a to-do list, so it jumps straight to it. */}
          <InvoicingKpi
            label="Need a final Számla"
            value={missingSummary ? missingSummary.no_szamla : '—'}
            detail={missingSummary ? `${missingSummary.no_documents} bookings have no documents` : ''}
            icon="fa-triangle-exclamation"
            accent="#dc2626"
            onClick={() => setTab('needs-final')}
          />
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Tabs activeKey={tab} onSelect={(k) => setTab(k || 'all')} className="mb-3">
            <Tab eventKey="all" title="All invoices">
              <AllInvoicesList
                documents={documents}
                loading={loading}
                onChanged={refreshAll}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                pagination={pagination}
                onPageChange={setPage}
                onLimitChange={(n) => { setLimit(n); setPage(1); }}
                facets={facets}
                paginated={paginated}
              />
            </Tab>
            <Tab
              eventKey="needs-final"
              title={(
                <span>
                  Needs final Számla
                  {missingSummary?.no_szamla > 0 && (
                    <Badge bg="danger" className="ms-2">{missingSummary.no_szamla}</Badge>
                  )}
                </span>
              )}
            >
              <NeedsFinalSzamla
                refreshKey={refreshKey}
                onSummaryChange={setMissingSummary}
                onChanged={refresh}
              />
            </Tab>
            <Tab
              eventKey="missing"
              title={(
                <span>
                  Missing documents
                  {missingSummary?.no_documents > 0 && (
                    <Badge bg="secondary" className="ms-2">{missingSummary.no_documents}</Badge>
                  )}
                </span>
              )}
            >
              <MissingDocuments
                refreshKey={refreshKey}
                onSummaryChange={setMissingSummary}
                onChanged={refresh}
              />
            </Tab>
            <Tab
              eventKey="failed"
              title={(
                <span>
                  Failed documents
                  {failedSummary?.groups_still_missing_document > 0 && (
                    <Badge bg="danger" className="ms-2">{failedSummary.groups_still_missing_document}</Badge>
                  )}
                </span>
              )}
            >
              <FailedDocuments
                refreshKey={refreshKey}
                onSummaryChange={setFailedSummary}
                onChanged={refresh}
              />
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} size="lg" backdrop="static" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Create a document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BillingBasisPicker onResolved={setResolved} />
          {resolved.bookings.length === 0 ? (
            <div className="text-muted small mt-3">
              <i className="fa fa-arrow-up me-1" />
              Pick bookings, an event or a partner above to see what&apos;s already invoiced.
            </div>
          ) : (
            <InvoicedSummaryTable
              bookings={resolved.bookings}
              partner={resolved.partner}
              basisLabel={resolved.label}
              onInvoiced={refreshAll}
            />
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}

export default Invoicing;
